/**
 * /financeiro/dashboard — Dashboard Financeiro v2 + "Personalizar layout" (FIN-LAYOUT).
 *
 * Layout do widget-grid é personalizável por usuário (persistido no backend em
 * PreferenciaDashboard.layout_financeiro via /api/me/dashboard-preferencia):
 *   - "Personalizar layout" entra em modo edição (.is-editing): arrastar p/
 *     reordenar, remover widget, "Adicionar widget".
 *   - Por widget: Período (override da janela global, fetch lazy), Personalizar
 *     (tipo/paleta/grade nos charts), Mais (Exportar CSV / Ocultar).
 *   - "Exportar" no header baixa o resumo da aba ativa em CSV.
 *
 * A KPI strip (zona 2 — contexto sticky) é fixa; só o widget-grid é editável.
 * Dados read-only de GET /api/financeiro/dashboard-executivo (1 request por
 * janela, cacheado). Nenhum cálculo financeiro foi alterado.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiArrowDownRight, FiArrowUpRight, FiCalendar, FiDownload, FiEdit2,
  FiMoreVertical, FiPlus, FiSettings, FiX, FiZap,
} from "react-icons/fi";

import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { obterDashboardExecutivo } from "../../../services/financeiro";
import { PageContainer } from "../../../styles/shared";
import { EmptyMsg } from "../../Orcamentos/styles";

import {
  CATALOGO_POR_ID, PALETAS, fmtK, layoutDefault, num,
} from "./_widgets";

const JANELAS = [{ v: 7, l: "7d" }, { v: 30, l: "30d" }, { v: 90, l: "90d" }];
const TIPOS_CHART = [{ id: "bar", nome: "Barras" }, { id: "line", nome: "Linhas" }, { id: "area", nome: "Área" }];
const DRAGGER = (
  <svg className="dragger" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="9" cy="6" r="1" /><circle cx="15" cy="6" r="1" /><circle cx="9" cy="12" r="1" />
    <circle cx="15" cy="12" r="1" /><circle cx="9" cy="18" r="1" /><circle cx="15" cy="18" r="1" />
  </svg>
);

function baixarCSV(nomeArq, secoes) {
  const linhas = [];
  for (const s of secoes) {
    if (s.nome) linhas.push(s.nome);
    linhas.push((s.colunas || []).join(";"));
    for (const l of s.linhas || []) linhas.push(l.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";"));
    linhas.push("");
  }
  const blob = new Blob(["﻿" + linhas.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = nomeArq; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { request } = useAuth();

  const [dadosPorJanela, setDadosPorJanela] = useState({});
  const [erro, setErro] = useState(null);
  const [aba, setAba] = useState("operacional");
  const [janela, setJanela] = useState(30);
  const [layout, setLayout] = useState(null); // null = carregando
  const [editando, setEditando] = useState(false);
  const [painelChart, setPainelChart] = useState(null); // widgetId | null
  const [catalogoAberto, setCatalogoAberto] = useState(false);
  const [menu, setMenu] = useState(null); // widgetId (kebab)
  const [periodo, setPeriodo] = useState(null); // widgetId (popover período)
  const [dragOver, setDragOver] = useState(null);
  const dragId = useRef(null);
  const fetched = useRef(new Set());
  const saveTimer = useRef(null);

  // ---- carga de dados por janela (cacheada, dedupe por ref) ----
  const carregarJanela = useCallback((j) => {
    if (fetched.current.has(j)) return;
    fetched.current.add(j);
    obterDashboardExecutivo(request, { janela_dias: j })
      .then((d) => setDadosPorJanela((prev) => ({ ...prev, [j]: d })))
      .catch((e) => { fetched.current.delete(j); setErro(e?.message || "Erro ao carregar dashboard."); });
  }, [request]);

  useEffect(() => { carregarJanela(janela); }, [janela, carregarJanela]);

  // ---- preferência de layout (carrega UMA vez; não re-aplica a cada
  // mudança de identidade do `request`, p/ não sobrescrever edição local) ----
  const carregouLayout = useRef(false);
  useEffect(() => {
    if (carregouLayout.current) return;
    carregouLayout.current = true;
    request("/api/me/dashboard-preferencia")
      .then((p) => setLayout(p?.layout_financeiro || {}))
      .catch(() => setLayout({}));
  }, [request]);

  // V15: guarda o pendente para o unmount conseguir descarregar. Sem isso,
  // arrumar o dashboard e sair em menos de 600ms perdia a mudanca, calada.
  const pendenteRef = useRef(null);

  const persistir = useCallback((novo) => {
    setLayout(novo);
    pendenteRef.current = novo;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const corpo = pendenteRef.current;
      pendenteRef.current = null;
      request("/api/me/dashboard-preferencia", {
        method: "PUT",
        body: JSON.stringify({ layout_financeiro: corpo }),
      }).catch((err) => {
        // Era `.catch(() => {})` — falha em salvar o layout sumia por
        // completo. O toast vem do `request`; aqui so nao engolimos.
        console.error("Falha ao salvar o layout do dashboard", err);
      });
    }, 600);
  }, [request]);

  // Flush no unmount (via ref, mesmo padrao de RevisorFerragens/ListaSalva):
  // se dependesse da identidade de `persistir`, o cleanup rodaria a cada
  // render e o debounce viraria um PUT por interacao.
  const descarregarRef = useRef(null);
  descarregarRef.current = () => {
    clearTimeout(saveTimer.current);
    const corpo = pendenteRef.current;
    pendenteRef.current = null;
    if (corpo) {
      request("/api/me/dashboard-preferencia", {
        method: "PUT",
        body: JSON.stringify({ layout_financeiro: corpo }),
      }).catch((err) => {
        console.error("Falha ao salvar o layout ao sair", err);
      });
    }
  };
  useEffect(() => () => descarregarRef.current?.(), []);

  const mutarAba = useCallback((fn) => {
    const cur = layout || {};
    persistir({ ...cur, [aba]: fn({ ...(cur[aba] || {}) }) });
  }, [layout, aba, persistir]);

  // ---- derivação do layout da aba ----
  const abaIds = useMemo(() => layoutDefault(aba), [aba]);
  const conf = (layout && layout[aba]) || {};
  const { visiveis, ocultos } = useMemo(() => {
    const salva = conf.ordem || [];
    const ord = [...salva.filter((id) => abaIds.includes(id)), ...abaIds.filter((id) => !salva.includes(id))];
    const oc = new Set(conf.ocultos || []);
    return { visiveis: ord.filter((id) => !oc.has(id)), ocultos: ord.filter((id) => oc.has(id)) };
  }, [conf.ordem, conf.ocultos, abaIds]);

  // garante fetch das janelas override usadas
  useEffect(() => {
    Object.values(conf.janelas || {}).forEach((j) => carregarJanela(j));
  }, [conf.janelas, carregarJanela]);

  // ---- mutações ----
  const reordenar = (dropId) => {
    const from = visiveis.indexOf(dragId.current);
    const to = visiveis.indexOf(dropId);
    setDragOver(null);
    if (from < 0 || to < 0 || from === to) return;
    const nv = [...visiveis];
    const [m] = nv.splice(from, 1);
    nv.splice(to, 0, m);
    mutarAba((a) => ({ ...a, ordem: [...nv, ...ocultos] }));
  };
  const ocultar = (id) => { setMenu(null); mutarAba((a) => ({ ...a, ocultos: [...new Set([...(a.ocultos || []), id])] })); };
  const mostrar = (id) => mutarAba((a) => ({ ...a, ocultos: (a.ocultos || []).filter((x) => x !== id) }));
  const definirJanelaWidget = (id, dias) => {
    setPeriodo(null);
    mutarAba((a) => { const js = { ...(a.janelas || {}) }; if (dias) js[id] = dias; else delete js[id]; return { ...a, janelas: js }; });
    if (dias) carregarJanela(dias);
  };

  const chartCfg = (id) => (layout?.charts?.[id]) || { tipo: "bar", paleta: "flux", grid: true };
  const setChartCfg = (id, patch) => {
    const cur = layout || {};
    persistir({ ...cur, charts: { ...(cur.charts || {}), [id]: { ...chartCfg(id), ...patch } } });
  };

  // fecha menus em clique fora / Esc
  useEffect(() => {
    if (menu == null && periodo == null) return undefined;
    const fechar = () => { setMenu(null); setPeriodo(null); };
    const onEsc = (e) => e.key === "Escape" && fechar();
    window.addEventListener("click", fechar);
    window.addEventListener("keydown", onEsc);
    return () => { window.removeEventListener("click", fechar); window.removeEventListener("keydown", onEsc); };
  }, [menu, periodo]);

  const exportarWidget = (id) => {
    const w = CATALOGO_POR_ID[id];
    const dados = dadosPorJanela[conf.janelas?.[id] || janela];
    if (!w || !dados) return;
    const sec = w.exportar(dados);
    baixarCSV(`${id}.csv`, [{ nome: w.titulo, ...sec }]);
    setMenu(null);
  };
  const exportarAba = () => {
    const secoes = visiveis.map((id) => {
      const w = CATALOGO_POR_ID[id];
      const dados = dadosPorJanela[conf.janelas?.[id] || janela];
      return dados ? { nome: w.titulo, ...w.exportar(dados) } : null;
    }).filter(Boolean);
    if (secoes.length) baixarCSV(`dashboard-${aba}.csv`, secoes);
  };

  const dadosGlobais = dadosPorJanela[janela];
  const op = dadosGlobais?.operacional || {};
  const dir = dadosGlobais?.diretor || {};
  const projetadoFim = (dadosGlobais?.fluxo_caixa?.buckets || []).slice(-1)[0]?.saldo_acumulado;

  return (
    <PageContainer style={{ maxWidth: "none" }} className="fin2">
      <div className="flex between" style={{ alignItems: "flex-end", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="breadcrumb">
            <span>Início</span><span className="crumb-sep">/</span>
            <span>Financeiro</span><span className="crumb-sep">/</span>
            <span className="active">Dashboard</span>
          </div>
          <h1 className="page-title">Dashboard Financeiro</h1>
          <div className="page-sub">
            Visão 360 · {editando ? <strong style={{ color: "var(--accent)" }}>arraste para reordenar · use ⋮ para exportar/ocultar</strong> : <>widgets personalizáveis em <strong style={{ color: "var(--accent)" }}>Personalizar layout</strong></>}
          </div>
        </div>
        <div className="flex gap-2 items-center" style={{ flexWrap: "wrap" }}>
          <div className="tabchip-group">
            {["operacional", "diretor"].map((a) => (
              <div key={a} className={"tabchip" + (aba === a ? " active" : "")} role="button" tabIndex={0}
                   onClick={() => setAba(a)} onKeyDown={(e) => e.key === "Enter" && setAba(a)}>
                {a === "operacional" ? "Operacional" : "Diretor"}
              </div>
            ))}
          </div>
          <div className="tabchip-group">
            {JANELAS.map((j) => (
              <div key={j.v} className={"tabchip" + (janela === j.v ? " active" : "")} role="button" tabIndex={0}
                   onClick={() => setJanela(j.v)} onKeyDown={(e) => e.key === "Enter" && setJanela(j.v)}>{j.l}</div>
            ))}
          </div>
          <button type="button" className="btn btn--ghost btn--sm" onClick={exportarAba}>
            <FiDownload size={14} aria-hidden="true" /> Exportar
          </button>
          <button type="button" className={"btn btn--sm " + (editando ? "btn--primary" : "btn--secondary")}
                  onClick={() => setEditando((e) => !e)}>
            <FiEdit2 size={14} aria-hidden="true" /> {editando ? "Concluir" : "Personalizar layout"}
          </button>
        </div>
      </div>

      {erro && <div className="empty"><div className="msg">{erro}</div></div>}

      {/* ===== KPI strip (zona contexto — fixa) ===== */}
      {aba === "operacional" && dadosGlobais && (
        <div className="kpi-strip-v2 standalone" style={{ marginBottom: 14 }}>
          <div className="kpi-v2"><div className="lbl">Saldo total</div><div className="val">{fmtK(op.saldo_total)}</div><div className="meta">consolidado</div></div>
          <div className="kpi-v2"><div className="lbl">Entradas {janela}d</div><div className="val">{fmtK(op.entradas_periodo)}</div><div className="meta"><span className="delta up"><FiArrowUpRight size={11} /> recebido</span></div></div>
          <div className="kpi-v2"><div className="lbl">Saídas {janela}d</div><div className="val">{fmtK(op.saidas_periodo)}</div><div className="meta"><span className="delta is-bad"><FiArrowDownRight size={11} /> pago</span></div></div>
          <div className="kpi-v2"><div className="lbl">A receber</div><div className="val">{fmtK(op.a_receber)}</div><div className="meta">próximos {janela}d</div></div>
          <div className="kpi-v2"><div className="lbl">A pagar</div><div className="val">{fmtK(op.a_pagar)}</div><div className="meta">próximos {janela}d</div></div>
          {projetadoFim != null && (
            <div className="next-action" style={{ background: "var(--info-bg)", borderLeftColor: "var(--info-border)" }}>
              <div className="lbl" style={{ color: "var(--info-text)" }}><FiZap size={13} aria-hidden="true" /> PROJEÇÃO</div>
              <div className="title" style={{ color: "var(--info-text)" }}>Saldo projetado (8 sem): {fmtK(projetadoFim)}</div>
              <button type="button" className="action-link" style={{ color: "var(--info-text)" }} onClick={() => navigate("/financeiro/fluxo-caixa")}>Ver fluxo →</button>
            </div>
          )}
        </div>
      )}
      {aba === "diretor" && dadosGlobais && (
        <div className="kpi-strip-v2 standalone" style={{ marginBottom: 14 }}>
          <div className="kpi-v2"><div className="lbl">Receita (competência)</div><div className="val">{fmtK(dir.receita_competencia)}</div><div className="meta">do mês</div></div>
          <div className="kpi-v2"><div className="lbl">Ganho (T)</div><div className="val">{fmtK(dir.ganho_t)}</div><div className="meta">receita − custos variáveis</div></div>
          <div className="kpi-v2"><div className="lbl">Despesa operacional (OE)</div><div className="val">{fmtK(dir.oe)}</div><div className="meta">do mês</div></div>
          <div className="kpi-v2"><div className="lbl">Resultado (T − OE)</div><div className="val" style={{ color: num(dir.resultado) >= 0 ? "var(--success-text)" : "var(--danger-text)" }}>{fmtK(dir.resultado)}</div><div className="meta">{dir.resultado_pct}% da receita</div></div>
          <div className="kpi-v2"><div className="lbl">Empréstimos em aberto</div><div className="val">{fmtK(dir.emprestimos_aberto)}</div><div className="meta">saldo devedor</div></div>
        </div>
      )}

      {/* ===== widget-grid personalizável ===== */}
      {layout != null && (
        <div className={"widget-grid" + (editando ? " is-editing" : "")}>
          {visiveis.map((id) => {
            const w = CATALOGO_POR_ID[id];
            if (!w) return null;
            const effJ = conf.janelas?.[id] || janela;
            const dados = dadosPorJanela[effJ];
            const Comp = w.Comp;
            const ehBloco = !!w.bloco;
            const temOverride = conf.janelas?.[id] != null;
            const sub = w.sub + (temOverride ? ` · ${effJ}d` : "");
            return (
              <div key={id} className={(ehBloco ? "block" : "cc") + " span-" + w.span}
                   style={{ position: "relative" }}
                   draggable={editando}
                   onDragStart={() => { dragId.current = id; }}
                   onDragOver={(e) => { if (editando) { e.preventDefault(); setDragOver(id); } }}
                   onDragLeave={() => editando && setDragOver((d) => (d === id ? null : d))}
                   onDrop={() => editando && reordenar(id)}
                   data-dragover={dragOver === id ? "1" : undefined}>
                <div className={ehBloco ? "block__head" : "cc__head"}>
                  <div style={{ minWidth: 0 }}>
                    <div className={ehBloco ? "block__title" : "cc__title"}>
                      {editando && DRAGGER}{w.titulo}
                    </div>
                    {!ehBloco && <div className="cc__sub">{sub}</div>}
                  </div>
                  <div className="cc__actions">
                    <button type="button" className={"cc__action" + (temOverride ? " primary" : "")} title="Período do widget"
                            onClick={(e) => { e.stopPropagation(); setMenu(null); setPeriodo((p) => (p === id ? null : id)); }}>
                      <FiCalendar size={14} aria-hidden="true" />
                    </button>
                    {w.chart && (
                      <button type="button" className="cc__action primary" title="Personalizar gráfico"
                              onClick={(e) => { e.stopPropagation(); setPainelChart(id); }}>
                        <FiSettings size={14} aria-hidden="true" />
                      </button>
                    )}
                    <button type="button" className="cc__action" title="Mais opções"
                            onClick={(e) => { e.stopPropagation(); setPeriodo(null); setMenu((m) => (m === id ? null : id)); }}>
                      <FiMoreVertical size={14} aria-hidden="true" />
                    </button>
                    {editando && (
                      <button type="button" className="cc__action" title="Remover do painel"
                              onClick={(e) => { e.stopPropagation(); ocultar(id); }}>
                        <FiX size={14} aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </div>

                {periodo === id && (
                  <div className="dash-pop" onClick={(e) => e.stopPropagation()}>
                    <div className="dash-pop__lbl">Período deste widget</div>
                    {JANELAS.map((j) => (
                      <button key={j.v} type="button" className={"dash-pop__item" + (effJ === j.v && temOverride ? " is-on" : "")}
                              onClick={() => definirJanelaWidget(id, j.v)}>{j.l}</button>
                    ))}
                    <button type="button" className={"dash-pop__item" + (!temOverride ? " is-on" : "")}
                            onClick={() => definirJanelaWidget(id, null)}>Padrão (global)</button>
                  </div>
                )}
                {menu === id && (
                  <div className="dash-pop" onClick={(e) => e.stopPropagation()}>
                    <button type="button" className="dash-pop__item" onClick={() => exportarWidget(id)}>Exportar CSV</button>
                    {w.chart && <button type="button" className="dash-pop__item" onClick={() => { setMenu(null); setPainelChart(id); }}>Personalizar gráfico</button>}
                    <button type="button" className="dash-pop__item danger" onClick={() => ocultar(id)}>Ocultar widget</button>
                  </div>
                )}

                <div className={ehBloco ? "block__body" : "cc__body"}>
                  {dados ? <Comp dados={dados} janela={effJ} chartCfg={w.chart ? chartCfg(id) : undefined} navigate={navigate} /> : <EmptyMsg>Carregando…</EmptyMsg>}
                </div>
              </div>
            );
          })}

          {editando && (
            <div className="widget--add widget--add-ghost span-12" style={{ minHeight: 80 }}
                 role="button" tabIndex={0} onClick={() => setCatalogoAberto(true)}
                 onKeyDown={(e) => e.key === "Enter" && setCatalogoAberto(true)}>
              <div className="icon"><FiPlus size={16} aria-hidden="true" /></div>
              <div>Adicionar widget</div>
              <div className="tiny muted">{ocultos.length ? `${ocultos.length} disponível(is)` : "todos no painel"}</div>
            </div>
          )}
        </div>
      )}

      {layout == null && <EmptyMsg>Carregando dashboard...</EmptyMsg>}

      {/* ===== Slide-over: Personalizar gráfico ===== */}
      <SlideOverPanel open={!!painelChart} onClose={() => setPainelChart(null)}
                      title="Personalizar gráfico"
                      subtitle={painelChart ? `${CATALOGO_POR_ID[painelChart]?.titulo} · aplicado em tempo real` : ""}
                      width="min(95vw, 420px)">
        {painelChart && (() => {
          const cfg = chartCfg(painelChart);
          return (
            <div className="fin2-panel">
              <div className="so-section-label">Tipo de gráfico</div>
              <div className="chart-type-grid">
                {TIPOS_CHART.map((t) => (
                  <button key={t.id} type="button" className={"chart-type" + (cfg.tipo === t.id ? " is-on" : "")}
                          onClick={() => setChartCfg(painelChart, { tipo: t.id })}>
                    <FiSettings size={18} aria-hidden="true" /><span className="nm">{t.nome}</span>
                  </button>
                ))}
              </div>
              <div className="so-section-label">Paleta</div>
              <div className="palette-grid" style={{ marginBottom: 18 }}>
                {Object.entries(PALETAS).map(([pid, p]) => (
                  <button key={pid} type="button" className={"palette-card" + (cfg.paleta === pid ? " is-on" : "")}
                          onClick={() => setChartCfg(painelChart, { paleta: pid })}>
                    <span className="strip">{p.cores.map((c, i) => <span key={i} style={{ background: c }} />)}</span>
                    <span className="nm">{p.nome}</span>
                  </button>
                ))}
              </div>
              <div className="so-section-label">Opções</div>
              <label className="flex between" style={{ fontSize: 13, padding: "8px 0", cursor: "pointer" }}>
                Exibir grade
                <button type="button" className={"switch" + (cfg.grid ? " is-on" : "")} role="switch" aria-checked={cfg.grid}
                        onClick={() => setChartCfg(painelChart, { grid: !cfg.grid })} />
              </label>
            </div>
          );
        })()}
      </SlideOverPanel>

      {/* ===== Slide-over: Adicionar widget ===== */}
      <SlideOverPanel open={catalogoAberto} onClose={() => setCatalogoAberto(false)}
                      title="Adicionar widget" subtitle="Widgets disponíveis para esta aba" width="min(95vw, 460px)">
        <div className="fin2-panel">
          {ocultos.length === 0 ? (
            <EmptyMsg>Todos os widgets desta aba já estão no painel.</EmptyMsg>
          ) : ocultos.map((id) => {
            const w = CATALOGO_POR_ID[id];
            if (!w) return null;
            return (
              <div key={id} className="flex between" style={{ padding: "12px 0", borderBottom: "1px solid var(--divider)" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{w.titulo}</div>
                  <div className="tiny muted">{w.sub}</div>
                </div>
                <button type="button" className="btn btn--secondary btn--sm" onClick={() => mostrar(id)}>
                  <FiPlus size={13} aria-hidden="true" /> Adicionar
                </button>
              </div>
            );
          })}
          <div className="tiny muted" style={{ marginTop: 16 }}>
            Os widgets refletem dados reais do financeiro. Para remover, use o ⋮ do widget ou o ✕ no modo edição.
          </div>
        </div>
      </SlideOverPanel>
    </PageContainer>
  );
}
